import { useId, useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { useSalesTagsQuery } from '@/client/api/sales/sales';

/** Matches the server's normalize_tags: trimmed, single-spaced, lowercase. */
const clean = (t: string) => t.trim().replace(/\s+/g, ' ').toLowerCase().slice(0, 32);

export function TagChips({ tags, className = '' }: { tags?: string[]; className?: string }) {
  if (!tags?.length) return null;
  return (
    <span className={`flex flex-wrap gap-1 ${className}`}>
      {tags.map((t) => (
        <span key={t} className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-800">
          {t}
        </span>
      ))}
    </span>
  );
}

/** Chip editor. Enter or comma adds; suggestions are the pool's existing tags,
 *  so reps reuse "hot" rather than inventing "hot lead" next to it. */
export function TagInput({
  value,
  onChange,
  id,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  id?: string;
}) {
  const listId = useId();
  const [draft, setDraft] = useState('');
  const known = useSalesTagsQuery().data ?? [];

  const add = (raw: string) => {
    const next = raw
      .split(/[,;]/)
      .map(clean)
      .filter((t) => t && !value.includes(t));
    if (next.length) onChange([...value, ...next].slice(0, 20));
    setDraft('');
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-lg border border-gray-400 bg-white px-2 py-1.5 focus-within:border-slate-900 focus-within:ring-2 focus-within:ring-slate-900">
      {value.map((t) => (
        <span
          key={t}
          className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-800"
        >
          {t}
          <button
            type="button"
            onClick={() => onChange(value.filter((x) => x !== t))}
            className="rounded-full text-indigo-700 hover:text-indigo-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
            aria-label={`Remove tag ${t}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        id={id}
        list={listId}
        autoComplete="off"
        className="min-w-[8rem] flex-1 border-0 bg-transparent p-0.5 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-0"
        value={draft}
        onChange={(e) => {
          // A datalist pick arrives as a replacement (no inputType in some
          // browsers) — add it; ordinary typing ("hot" on the way to
          // "hotel") only updates the draft.
          const v = e.target.value;
          const inputType = (e.nativeEvent as InputEvent).inputType;
          const picked = !inputType || inputType === 'insertReplacementText';
          if (picked && known.some((k) => k.tag === v)) add(v);
          else setDraft(v);
        }}
        onKeyDown={onKey}
        onBlur={() => draft && add(draft)}
        placeholder={value.length ? 'Add another' : 'Add tags, e.g. hot, agency'}
      />
      <datalist id={listId}>
        {known
          .filter((k) => !value.includes(k.tag))
          .map((k) => (
            <option key={k.tag} value={k.tag}>
              {k.count} prospect{k.count === 1 ? '' : 's'}
            </option>
          ))}
      </datalist>
    </div>
  );
}
